import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { login } from "redux/auth/AuthSlice";
import * as z from "zod";

import { createAxiosInstance } from "api/axiosConfig";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useHandleNetworkError } from "utils/useHandleNetworkError";
import {
  isAcceptedNmbiNumber,
  isAcceptedPhoneNumber,
  NMBI_NUMBER_ERROR,
  PHONE_NUMBER_ERROR,
} from "utils/validators";

const phoneSchema = z
  .string()
  .optional()
  .refine(isAcceptedPhoneNumber, { message: PHONE_NUMBER_ERROR });

export const profileSchema = z.object({
  title: z.string(),
  firstName: z.string().min(1, "Please enter first name."),
  lastName: z.string().min(1, "Please enter last name."),
  gender: z.string().optional(),
  email: z.string().min(1, "Please enter email."),
  mobile: phoneSchema,
  homeNumber: z.string().optional(),
  outOfHoursNumber: phoneSchema,
  specialism: z.string().optional(),
  qualification: z.string().optional(),
  professionalMedicalNumber: z.string().optional(),
  gmsNumber: z.string().optional(),
  deaspPanelNumber: z.string().optional(),
  // A nurse's NMBI number is kept apart from the Medical Council number, which referrals and lab
  // orders send as a doctor's MCN (KIN-2111). The backend refuses a malformed one with a 400.
  nmbiNumber: z
    .string()
    .optional()
    .refine(isAcceptedNmbiNumber, { message: NMBI_NUMBER_ERROR }),
});

export const useProfileForm = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const handleNetworkError = useHandleNetworkError("Error saving profile");

  const tenantVal = localStorage.getItem("kinesin-tenant");

  const [selectedImage, setSelectedImage] = useState(null);
  const [base64Image, setBase64Image] = useState("");
  const fileInputRef = useRef(null);

  const { data: staffInfo } = useStaffInfo();

  const methods = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      title: "Mr.",
      firstName: "",
      lastName: "",
      gender: "",
      email: "",
      mobile: "",
      homeNumber: "",
      outOfHoursNumber: "",
      specialism: "",
      qualification: "",
      professionalMedicalNumber: "",
      gmsNumber: "",
      deaspPanelNumber: "",
      nmbiNumber: "",
    },
  });

  useEffect(() => {
    if (!staffInfo) return;
    const member = staffInfo.staffMember || staffInfo;

    setBase64Image(member.profilePic || "");
    methods.reset({
      title: member.title || "",
      firstName: member.firstName || member.name || "",
      lastName: member.lastName || "",
      gender: member.gender || "",
      email: member.email || "",
      mobile: member.mobile || "",
      homeNumber: member.homeNumber || "",
      outOfHoursNumber: member.outOfHoursNumber || "",
      specialism: member.specialism || "",
      qualification: member.qualification || "",
      professionalMedicalNumber: member.professionalMedicalNumber || "",
      gmsNumber: member.gmsNumber || "",
      deaspPanelNumber: member.deaspPanelNumber || "",
      nmbiNumber: member.nmbiNumber || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffInfo]);

  const { mutate: saveProfile } = useMutation({
    // The hook toasts the server's own reason (a refused GMS or NMBI number says which), so the
    // global handler's generic toast would only stack on top of it.
    meta: { skipGlobalError: true },
    mutationFn: async (data) => {
      const axiosInstance = createAxiosInstance();

      await axiosInstance.put(`${tenantVal}/staffmember/profile`, {
        title: data.title,
        firstName: data.firstName,
        lastName: data.lastName,
        gender: data.gender,
        mobile: data.mobile,
        homeNumber: data.homeNumber,
        outOfHoursNumber: data.outOfHoursNumber,
        specialism: data.specialism,
        qualification: data.qualification,
        professionalMedicalNumber: data.professionalMedicalNumber,
        gmsNumber: data.gmsNumber,
        deaspPanelNumber: data.deaspPanelNumber,
        nmbiNumber: data.nmbiNumber,
      });

      return data;
    },
    onSuccess: (data) => {
      toast.success("Profile saved successfully");

      const updatedName = `${data.firstName} ${data.lastName}`.trim();
      localStorage.setItem("kinesin-name", updatedName);

      if (currentUser) {
        dispatch(login({ user: { ...currentUser, name: updatedName } }));
      }

      queryClient.invalidateQueries({ queryKey: ["staffInfo"] });
      queryClient.invalidateQueries({ queryKey: ["whoami"] });
    },
    onError: handleNetworkError,
  });

  const handleCameraIconClick = () => {
    fileInputRef.current.click();
  };

  const handleFileSelection = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setBase64Image(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = (data) => saveProfile(data);

  return {
    methods,
    selectedImage,
    base64Image,
    fileInputRef,
    handleCameraIconClick,
    handleFileSelection,
    onSubmit,
    // The form is only a copy of the record once the record has arrived. Saving before that, or
    // after the read failed, would send every field as "", which the backend now treats as
    // "clear it" (KIN-1724, KIN-2133).
    canSave: !!staffInfo,
  };
};
